import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import api from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import BlockEditor from "@/components/BlockEditor";
import { inputCls, labelCls, Field } from "./ui";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";

export default function ProgramManager() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/program").then((r) => setP(r.data)).catch(() => {}); }, []);

  const setH = (patch) => setP((x) => ({ ...x, home: { ...x.home, ...patch } }));
  const setDay = (idx, patch) => setP((x) => ({ ...x, days: x.days.map((d, i) => (i === idx ? { ...d, ...patch } : d)) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { home: p.home, intro: p.intro, ebook: p.ebook, path_intro: p.path_intro, quaderno: p.quaderno, days: p.days };
      const r = await api.put("/admin/program", payload);
      setP(r.data);
      toast.success("Prodotto salvato");
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };

  if (!p) return <Loader2 className="w-6 h-6 animate-spin text-[#C6899E]" />;

  const includes = p.home.includes || [];

  return (
    <div data-testid="cms-program" className="space-y-8">
      <div className="flex items-center justify-between sticky top-20 bg-[#FFFFFF] z-10 py-2">
        <h2 className="font-serif text-2xl">Prodotto · eBook + Percorso 21 giorni</h2>
        <button onClick={save} disabled={saving} data-testid="program-save"
          className="inline-flex items-center gap-2 bg-[#C6899E] text-[#FBF5F1] rounded-full px-6 py-2.5 text-sm uppercase tracking-[0.12em] hover:bg-[#B4718C] disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva tutto
        </button>
      </div>

      {/* HOME CARD */}
      <section className="rounded-2xl border border-[rgba(61,50,56,0.1)] p-5 space-y-4">
        <h3 className="font-serif text-xl">Scheda in Home</h3>
        <FileUpload label="Immagine principale" value={p.home.image_path} onChange={(v) => setH({ image_path: v })} testid="program-home-image" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Titolo"><input className={inputCls} value={p.home.title} onChange={(e) => setH({ title: e.target.value })} data-testid="program-home-title" /></Field>
          <Field label="Pulsante di acquisto (testo)"><input className={inputCls} value={p.home.button_label} onChange={(e) => setH({ button_label: e.target.value })} /></Field>
        </div>
        <Field label="Descrizione breve"><textarea rows={3} className={inputCls} value={p.home.description} onChange={(e) => setH({ description: e.target.value })} /></Field>
        <Field label="Testi (paragrafo esteso)"><textarea rows={4} className={inputCls} value={p.home.texts} onChange={(e) => setH({ texts: e.target.value })} /></Field>

        <Field label="Cosa comprende">
          <div className="space-y-2">
            {includes.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <input className={inputCls} value={it} onChange={(e) => setH({ includes: includes.map((x, idx) => (idx === i ? e.target.value : x)) })} />
                <button onClick={() => setH({ includes: includes.filter((_, idx) => idx !== i) })} className="p-2 text-[#9C8F94] hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setH({ includes: [...includes, ""] })} className="text-xs text-[#C6899E] inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Voce</button>
          </div>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Info eBook"><textarea rows={3} className={inputCls} value={p.home.ebook_info} onChange={(e) => setH({ ebook_info: e.target.value })} /></Field>
          <Field label="Info percorso 21 giorni"><textarea rows={3} className={inputCls} value={p.home.path_info} onChange={(e) => setH({ path_info: e.target.value })} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Prezzo (€)"><input type="number" step="0.01" className={inputCls} value={p.home.price} onChange={(e) => setH({ price: parseFloat(e.target.value) || 0 })} data-testid="program-home-price" /></Field>
          <Field label="Stato">
            <select className={inputCls} value={p.home.status} onChange={(e) => setH({ status: e.target.value })} data-testid="program-home-status">
              <option value="draft">Bozza</option><option value="published">Pubblicato</option>
            </select>
          </Field>
        </div>
      </section>

      {/* PRODUCT PAGE SECTIONS */}
      <section className="rounded-2xl border border-[rgba(61,50,56,0.1)] p-5 space-y-4">
        <h3 className="font-serif text-xl">Pagina prodotto (dopo l'acquisto)</h3>
        <Field label="Introduzione iniziale"><textarea rows={4} className={inputCls} value={p.intro.text} onChange={(e) => setP({ ...p, intro: { text: e.target.value } })} /></Field>

        <div className="border-t border-[rgba(61,50,56,0.08)] pt-4">
          <h4 className="font-serif text-lg mb-3">eBook</h4>
          <FileUpload label="Copertina eBook" value={p.ebook.cover_path} onChange={(v) => setP({ ...p, ebook: { ...p.ebook, cover_path: v } })} />
          <Field label="Descrizione breve"><textarea rows={2} className={inputCls} value={p.ebook.description} onChange={(e) => setP({ ...p, ebook: { ...p.ebook, description: e.target.value } })} /></Field>
          <FileUpload label="File PDF eBook" accept="*" value={p.ebook.pdf_path} onChange={(v) => setP({ ...p, ebook: { ...p.ebook, pdf_path: v } })} testid="program-ebook-pdf" />
        </div>

        <div className="border-t border-[rgba(61,50,56,0.08)] pt-4">
          <h4 className="font-serif text-lg mb-3">Introduzione al percorso di 21 giorni</h4>
          <textarea rows={4} className={inputCls} value={p.path_intro.text} onChange={(e) => setP({ ...p, path_intro: { text: e.target.value } })} />
        </div>

        <div className="border-t border-[rgba(61,50,56,0.08)] pt-4">
          <h4 className="font-serif text-lg mb-3">Quaderno dei 21 giorni</h4>
          <Field label="Testo introduttivo (editabile)"><textarea rows={3} className={inputCls} value={p.quaderno.text} onChange={(e) => setP({ ...p, quaderno: { ...p.quaderno, text: e.target.value } })} /></Field>
          <FileUpload label="PDF del Quaderno" accept="*" value={p.quaderno.pdf_path} onChange={(v) => setP({ ...p, quaderno: { ...p.quaderno, pdf_path: v } })} testid="program-quaderno-pdf" />
        </div>
      </section>

      {/* 21 DAYS */}
      <section className="rounded-2xl border border-[rgba(61,50,56,0.1)] p-5">
        <h3 className="font-serif text-xl mb-4">Le 21 stanze</h3>
        <Accordion type="single" collapsible className="space-y-3">
          {p.days.map((d, i) => (
            <AccordionItem key={d.day} value={`d-${d.day}`} className="rounded-xl border border-[rgba(61,50,56,0.1)] px-4 overflow-hidden" data-testid={`program-day-${d.day}`}>
              <AccordionTrigger className="hover:no-underline py-4">
                <span className="font-serif text-lg text-left"><span className="text-[#C6899E] mr-2">Giorno {d.day}</span>{d.title && <span className="text-[#8C7E83] text-base">— {d.title}</span>}</span>
              </AccordionTrigger>
              <AccordionContent className="pb-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Titolo del giorno"><input className={inputCls} value={d.title} onChange={(e) => setDay(i, { title: e.target.value })} data-testid={`program-day-title-${d.day}`} /></Field>
                  <Field label="Parola del giorno"><input className={inputCls} value={d.word} onChange={(e) => setDay(i, { word: e.target.value })} data-testid={`program-day-word-${d.day}`} /></Field>
                </div>
                <FileUpload label="Copertina del giorno" value={d.cover_path} onChange={(v) => setDay(i, { cover_path: v })} />
                <div>
                  <label className={labelCls}>Contenuto della stanza</label>
                  <BlockEditor blocks={d.blocks} onChange={(b) => setDay(i, { blocks: b })} testid={`program-blocks-${d.day}`} />
                </div>
                <div className="border-t border-[rgba(61,50,56,0.08)] pt-4">
                  <label className={labelCls}>Contenuto finale (dopo la parola del giorno)</label>
                  <BlockEditor blocks={d.word_content} onChange={(b) => setDay(i, { word_content: b })} testid={`program-wordcontent-${d.day}`} />
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#C6899E] text-[#FBF5F1] rounded-full px-6 py-2.5 text-sm uppercase tracking-[0.12em] hover:bg-[#B4718C] disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva tutto
        </button>
      </div>
    </div>
  );
}
